import React, { useEffect, useRef, useState } from "react";
import { cx } from "../utils";

// RAG priority pill — high/critical = red, medium = amber, low = green. Shown on
// the right of the header so every signal card surfaces its severity the same way.
const SEVERITY_PILL = {
  critical: {
    label: "Critical",
    bg: "var(--cc-danger-soft)",
    fg: "var(--cc-danger)",
  },
  high: {
    label: "High",
    bg: "var(--cc-danger-soft)",
    fg: "var(--cc-danger)",
  },
  medium: {
    label: "Medium",
    bg: "var(--cc-warning-soft)",
    fg: "var(--cc-warning)",
  },
  low: {
    label: "Low",
    bg: "var(--cc-success-soft)",
    fg: "var(--cc-success)",
  },
};

// Descriptions clamp to 2 lines; the quiet Read-more toggle expands in place and
// only renders when the text actually overflows (or clearly would). State is
// per-card because each card renders its own ClampedDescription instance.
const ClampedDescription = ({ text }) => {
  const bodyRef = useRef(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = bodyRef.current;
    if (el) setOverflows(el.scrollHeight > el.clientHeight + 1);
  }, [text]);

  // Fallback for when measurement isn't possible (first paint, tests): ~2 lines
  // of body copy at card width is well under 180 characters.
  const showToggle =
    expanded || overflows || (typeof text === "string" && text.length > 180);

  return (
    <div
      className={cx(
        "cc-alert-card__description-wrap",
        expanded && "is-expanded",
      )}
    >
      <p ref={bodyRef} className="cc-alert-card__description">
        {text}
      </p>
      {showToggle ? (
        <button
          type="button"
          className="cc-alert-card__read-more"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
        >
          {expanded ? "Show less ▴" : "Read more ▾"}
        </button>
      ) : null}
    </div>
  );
};

/**
 * CcAlertCard — one signal in the unified signal-card style: card + left accent
 * strip, a leading type pill (OBSERVATION / …), title, an optional meta-chip
 * footer (Review due / Evidence / date), an expandable evidence panel, and
 * right-aligned actions. Shares its anatomy with AnomalyConflictCard + the
 * pathway suggestion cards so every signal reads as one system.
 */
const CcAlertCard = ({
  title,
  description,
  severityStrip,
  muted = false,
  sourceLabel,
  severity,
  tags = [],
  date,
  attribution,
  evidenceExpanded = false,
  onEvidenceToggle,
  evidencePanel = null,
  actions = null,
  className,
  // Absorbed for back-compat — the status tick was removed in the unified card,
  // but consumers still pass these; keep them out of the spread to the DOM.
  // eslint-disable-next-line no-unused-vars
  statusIcon,
  // eslint-disable-next-line no-unused-vars
  sourceDotTone,
  // eslint-disable-next-line no-unused-vars
  onStatusClick,
  ...props
}) => (
  <article
    className={cx(
      "cc-alert-card",
      muted && "cc-alert-card--muted",
      actions && "cc-alert-card--has-actions",
      className,
    )}
    {...props}
    style={
      severityStrip
        ? { borderLeftColor: severityStrip, borderLeftWidth: "3px" }
        : undefined
    }
  >
    <div className="cc-alert-card__body">
      <div className="cc-alert-card__header">
        {sourceLabel ? (
          <span className="cc-alert-card__kind">{sourceLabel}</span>
        ) : null}

        <h3 className="cc-alert-card__title">{title}</h3>

        {SEVERITY_PILL[severity] ? (
          <span
            style={{
              marginLeft: "auto",
              alignSelf: "flex-start",
              flexShrink: 0,
              fontSize: 11,
              fontWeight: 700,
              padding: "2px 9px",
              borderRadius: 20,
              whiteSpace: "nowrap",
              background: SEVERITY_PILL[severity].bg,
              color: SEVERITY_PILL[severity].fg,
            }}
          >
            {SEVERITY_PILL[severity].label}
          </span>
        ) : null}
      </div>

      {description ? <ClampedDescription text={description} /> : null}

      {(tags.length > 0 || date || attribution || actions) && (
        <div className="cc-alert-card__footer">
          <div className="cc-alert-card__meta-row">
            {tags.map((tag) => {
              const isEvidence = tag.tone === "evidence";
              // Any tag with a click handler is a real button (pointer cursor,
              // focusable) — evidence keeps its dedicated toggle wiring.
              const isButton = Boolean(
                (isEvidence && onEvidenceToggle) || tag.onClick,
              );
              const TagEl = isButton ? "button" : "span";

              return (
                <TagEl
                  key={tag.key}
                  type={isButton ? "button" : undefined}
                  className={cx(
                    "cc-alert-card__tag",
                    tag.tone && `cc-alert-card__tag--${tag.tone}`,
                    isEvidence && evidenceExpanded && "is-expanded",
                  )}
                  onClick={
                    isEvidence && onEvidenceToggle
                      ? (e) => {
                          e.stopPropagation();
                          onEvidenceToggle();
                        }
                      : tag.onClick
                  }
                >
                  {tag.label}
                  {isEvidence ? (
                    <svg
                      className="cc-alert-card__tag-chevron"
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      aria-hidden
                    >
                      <path
                        d="M3 4.5L6 7.5L9 4.5"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        fill="none"
                      />
                    </svg>
                  ) : null}
                </TagEl>
              );
            })}

            {date ? <span className="cc-alert-card__meta">{date}</span> : null}

            {attribution ? (
              <span className="cc-alert-card__meta cc-alert-card__meta--attribution">
                <span className="cc-alert-card__meta-dot" aria-hidden />
                {attribution}
              </span>
            ) : null}
          </div>

          {actions ? (
            <div className="cc-alert-card__row-actions">{actions}</div>
          ) : null}
        </div>
      )}

      {evidenceExpanded && evidencePanel ? (
        <div className="cc-alert-card__evidence">{evidencePanel}</div>
      ) : null}
    </div>
  </article>
);

export default CcAlertCard;
